import React, { useState, useEffect } from 'react';
import axiosInstance from '../../axiosInstance';
import { AtomSpinner } from 'react-epic-spinners';
import UserTable from './DatabaseTables/UserTable';

import ProgressHistoryTable from './DatabaseTables/ProgressHistoryTable';
import TranscriptTable from './DatabaseTables/TranscriptTable';
import SupporterPass from './DatabaseTables/SupporterPass';
import ContentMetadata from './DatabaseTables/ContentMetadata';
import PaymentData from './DatabaseTables/PaymentData';
import { FaSearch, FaChevronLeft, FaChevronRight, FaTimes, FaDatabase } from 'react-icons/fa';
import './DatabaseViewer.css';


const API_URL = process.env.REACT_APP_API_URL;

const DatabaseViewer = ({ onClose }) => {
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState('Users');
  const [tableData, setTableData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [progressHistoryData, setProgressHistoryData] = useState([]);
  const [usersData, setUsersData] = useState([]);
  const currentYearRange = getCurrentYearRange();
  const currentMonthRange = getCurrentMonthRange();
  const currentMonthName = getMonthName(new Date().getMonth());

  // Helper functions to get current year and month ranges
  const getCurrentYearRange = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), 0, 1); // January 1st of current year
    const end = new Date(now.getFullYear(), 11, 31, 23, 59, 59); // December 31st of current year
    return { start, end };
  };

  const getCurrentMonthRange = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1); // First day of current month
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59); // Last day of current month
    return { start, end };
  };

  const getMonthName = (monthIndex) => {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return months[monthIndex];
  };

  // Fetch list of tables
  useEffect(() => {
    const fetchTables = async () => {
      setIsLoading(true);
      try {
        const response = await axiosInstance.get(`${API_URL}/api/data/database/tables`);
        setTables(response.data);
        const usersIndex = response.data.indexOf('Users');
        if (usersIndex !== -1) {
          setCurrentIndex(usersIndex);
          setSelectedTable('Users');
        
        }
      } catch (error) {
        console.error('Error fetching tables:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchTables();
  }, []);

  // Fetch data for selected table
  useEffect(() => {
    if (selectedTable) {
      const fetchTableData = async () => {
        setIsLoading(true);
        try {
          // Fetch the main table data
          const tableResponse = await axiosInstance.get(
            `${API_URL}/api/data/database/table/${selectedTable}`
          );
          const data = tableResponse.data?.data || [];
          setTableData(Array.isArray(data) ? data : []);
          if (selectedTable === 'Users') {
            setUsersData(data);
          }
          // If we're loading the Transcript table, also fetch progress history
          if (selectedTable === 'Transcript') {
            const progressResponse = await axiosInstance.get(
              `${API_URL}/api/data/database/table/ProgressHistory`
            );
            setProgressHistoryData(progressResponse.data?.data || []);
          }
        } catch (error) {
          console.error('Error fetching table data:', error);
          setTableData([]);
          setProgressHistoryData([]);
        } finally {
          setIsLoading(false);
        }
      };
      fetchTableData();
    }
  }, [selectedTable]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchTerm && tables.includes(searchTerm)) {
      setSelectedTable(searchTerm);
      setCurrentIndex(tables.indexOf(searchTerm));
    }
  };

  const navigateTable = (direction) => {
    const newIndex = direction === 'next' 
      ? (currentIndex + 1) % tables.length 
      : (currentIndex - 1 + tables.length) % tables.length;
    
    setCurrentIndex(newIndex);
    setSelectedTable(tables[newIndex]);
    setSearchTerm(tables[newIndex]);
  };

  

  const renderTableContent = () => {
    if (isLoading) return <AtomSpinner />;

    switch(selectedTable) {
      case 'Users':
        return <UserTable data={tableData} />;
      case 'Transcript':
        return <TranscriptTable 
          data={tableData} 
          progressHistoryData={progressHistoryData}
          usersData={usersData} 
        />;
      case 'ProgressHistory':
        return <ProgressHistoryTable data={tableData} 
         usersData={usersData} 
         />;
      case 'SupporterPass':
        return <SupporterPass data={tableData} />;
      case 'ContentMetadata':
        return <ContentMetadata 
        data={tableData}
        onDelete={handleDeleteContent} />;
      case 'PaymentData':
        return <PaymentData 
        data={tableData} 
        currentYearRange = {currentYearRange}
        currentMonthRange = {currentMonthRange}
        currentMonthName = {currentMonthName}
        onClose={() => setSelectedTable('Users')} />;
      
    }
  };
  const isValidId = (id) => /^[a-f0-9-]{36}$/.test(id); // UUID pattern

  const handleDeleteContent = async (contentId) => {
    if (!isValidId(contentId)) {
      alert('Invalid ID format');
      return;
    }
    try {
      // Execute the DELETE query
      await axiosInstance.post(`${API_URL}/api/data/database/query`, {
        query: `DELETE FROM ContentMetadata WHERE id = '${contentId}'`
      });
      
      // Refresh the data
      const response = await axiosInstance.get(
        `${API_URL}/api/data/database/table/ContentMetadata`
      );
      setTableData(response.data?.data || []);
    } catch (error) {
      console.error('Error deleting content:', error);
      // Consider rolling back UI changes if needed
    }
  };

  return (
    <div className="database-viewer">
      <div className="viewer-header">
        <div className="header-left">
          <h2>
            <FaDatabase className="header-icon" />
            Database Explorer
          </h2>
          <div className="table-navigation">
            <button 
              onClick={() => navigateTable('prev')}
              disabled={isLoading}
            >
              <FaChevronLeft />
            </button>
            <form onSubmit={handleSearch}>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search tables..."
                list="tables-list"
              />
              <datalist id="tables-list">
                {tables.map((table) => (
                  <option key={table} value={table} />
                ))}
              </datalist>
              <button type="submit">
                <FaSearch />
              </button>
            </form>
            <button 
              onClick={() => navigateTable('next')}
              disabled={isLoading}
            >
              <FaChevronRight />
            </button>
          </div>
        </div>
        <button className="close-btn" onClick={onClose}>
          <FaTimes />
        </button>
      </div>

      <div className="viewer-content">
        {selectedTable && (
          <div className="table-header">
            <h3>{selectedTable}</h3>
            <span className="table-stats">
              {tableData.length} records
              {selectedTable === 'Transcript' && progressHistoryData.length > 0 && (
                <span> | {progressHistoryData.length} progress records</span>
              )}
            </span>
          </div>
        )}
        {renderTableContent()}
      </div>
    </div>
  );
};

export default DatabaseViewer;